<template>
  <div
    class="sword-radio-group-container-wuk"
    :class="styleTypeModifier"
    @focus.capture="handleOnFocus"
    @blur.capture="handleOnBlur"
  >
    <slot name="label">
      <input-label v-if="label"
                   :label="label"
                   :style-type="labelStyle"
                   :info-text="labelInfo"
                   :required="required && showRequiredStar"
                   :disabled="disabled"
      ></input-label>
    </slot>

    <slot name="before-input"></slot>

    <div class="sword-radio-input-wrapper-wuk">
      <sword-radio v-for="(opt, key) in options"
                   :key="key"
                   v-model="selected"
                   :name="name"
                   :icon="variant === 'normal' ? opt.icon : ''"
                   :opt-value="opt.optValue"
                   :required="required"
                   :disabled="disabled || opt.disabled"
                   :class="{'invalid-field-wuk': invalidField && !focused }">
        {{ $t(opt.optLabel) }}
      </sword-radio>
    </div>
    <input-footer-wuk
      v-if="!hideErrors"
      :is-invalid="invalidField && !focused"
      :validator="validation"
      :manual-error="manualError"
      :help-text="helpText"
    />
  </div>
</template>

<script>
import inputHandler from '@ui-kit/mixins/inputHandler';
import InputLabel from '@ui-kit/components/inputs/labels/InputLabel.vue';
import SwordRadio from '@ui-kit/components/inputs/radios/SwordRadio.vue';
import InputFooterWuk from '@ui-kit/components/inputs/footnotes/InputFooter.vue';
import { useDebounceFn } from '@vueuse/core';

export default {
  name: 'SwordRadioGroupWuk',
  mixins: [inputHandler],
  components: {
    InputLabel,
    SwordRadio,
    InputFooterWuk,
  },
  model: {
    prop: 'checked',
    event: 'change',
  },
  props: {
    variant: {
      type: String,
      default: () => 'normal',
      validator(variant) {
        return ['normal', 'minimal'].includes(variant);
      },
    },
    styleType: {
      type: String,
      default: 'SwordRadio',
    },
    checked: {
      type: [String, Number],
      required: true,
    },
    state: {
      type: Object,
      default: () => ({}),
    },
    name: {
      type: String,
      default: null,
    },
    options: {
      type: Array,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    styleTypeModifier() {
      let styleClass = this.styleType;

      if (this.styleType !== 'SwordRadioStacked') {
        if (this.variant === 'minimal' || this.styleType === 'SwordRadio') {
          styleClass = 'SwordRadio';
        }

        if (this.variant === 'normal' || this.styleType === 'SwordRadioFat') {
          styleClass = 'SwordRadioFat';
        }
      }

      if (this.fillBg) {
        styleClass += ' --bg-fill';
      }

      return `--${styleClass}`;
    },
    selected: {
      get() {
        return this.checked;
      },
      set(opt) {
        this.$emit('change', opt);
      },
    },
  },
  methods: {
    handleOnFocus() {
      this.focused = true;
    },
    handleOnBlur() {
      this.focused = false;

      const debouncedFn = useDebounceFn(() => {
        if (!this.focused && this.validation && this.validation.$touch) {
          this.validation.$touch();
        }
      }, 300);

      debouncedFn();
    },
  },
};
</script>

<style lang="scss" scoped>
.sword-radio-group-container-wuk::v-deep {
  margin: $input-margin-wuk;

  .sword-radio-input-wrapper-wuk {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    align-items: flex-start;
  }

  &.-horizontal {
    div.field-wrapper-wuk {
      margin: $input-margin-inline-wuk;
      margin-top: 0;

      @media (max-width: $max-tablet-size) {
        margin-top: $input-vertical-spacing-wuk;
      }
    }

    .sword-radio-input-wrapper-wuk {
      flex-direction: row;
      min-width: 100%;
      margin-left: -$input-horizontal-spacing-wuk;
      margin-right: -$input-horizontal-spacing-wuk;
    }
  }

  &:not(.-horizontal) {
    flex-direction: column;

    div.field-wrapper-wuk {
      margin-bottom: 1rem;

      &:first-of-type {
        margin-top: 0;
      }

      &:last-of-type {
        margin-bottom: 0;
      }
    }

    .sword-radio-input-wrapper-wuk {
      max-width: 100%;
    }
  }

  input[type='radio'] {
    &:disabled {
      &:checked {
        @include toggle-item-tick-box-wuk {
          background: $context-color-7;
          box-shadow: inset 0px 0px 0px $radio-inset-shadow-size-wuk white;
        }
      }

      & + label .sword-icon-box {
        background: $context-color-6;
        color: $context-color-4;
      }
    }

    &:checked {
      &:not(:disabled) {
        @include toggle-item-tick-box-wuk {
          background: $toggle-input-checked-background-color-wuk !important;
          box-shadow: inset 0px 0px 0px $radio-inset-shadow-size-wuk white;
          border-color: $toggle-input-checked-background-color-wuk;
        }

        & + label .sword-icon-box {
          background: $toggle-input-icon-checked-background-color;
        }
      }
    }

    &:not(:disabled):not(:checked) {
      &:hover {
        @include toggle-item-tick-box-wuk {
          border-color: $toggle-input-hover-border-color-wuk;
        }
      }
    }

    &:hover {
      & + label .sword-icon-box {
        background: $toggle-input-icon-hover-background-color;
      }
    }

    & + .sword-radio-label-display {
      display: flex;
      align-items: center;
      flex-direction: row;

      .sword-icon-box {
        padding: 0.625rem;
        border-radius: 0.75rem;
        background: $toggle-input-icon-background-color;
        display: flex;
        align-items: center;
        margin-right: 1rem;
      }

      &.icon {
        padding: 0.75rem 1rem;
      }
    }
  }
  .invalid-field-wuk {
    &.minimal {
      input[type='radio']:not(:disabled) {
        @include toggle-item-tick-box-wuk {
          border-color: $input-error-color;
        }
      }
    }

    &:not(.minimal) {
      label {
        border-color: $input-error-color !important;
      }
    }
  }

  &.--SwordRadio {
    input[type='radio'] {

      &:disabled {
        @include toggle-structure-root-wuk {
          color: $input-disabled-text-color-wuk;
        }
        @include toggle-item-tick-box-wuk {
          border-color: $input-disabled-border-color-wuk;
          background-color: $input-disabled-background-color-wuk;
        }
      }
    }
  }

  &.--SwordRadioStacked {
    .sword-radio-input-wrapper-wuk {
      flex-wrap: nowrap;
      align-items: normal;
      margin-left: 0;
      margin-right: 0;

      @media (max-width: $max-tablet-size) {
        flex-direction: column;
      }
    }

    div.sword-radio-wuk {
      flex-grow: 1;
      flex-shrink: 1;
      flex-basis: 100%;
      border: $input-border-wuk;
      border-right-width: 0;
      margin-left: 0;
      margin-right: 0;
      display: flex;
      align-items: center;

      input[type='radio'] + label {
        border-radius: 0;

        @media (max-width: $max-tablet-size) {
          border-radius: $input-radius-wuk;
          border-right-width: $input-border-thickness-wuk;
          margin-top: $stacked-input-vertical-spacing-wuk;
          margin-bottom: $stacked-input-vertical-spacing-wuk;
        }
      }


      &:last-of-type {
        input[type='radio'] + label {
          border-right-width: $input-border-thickness-wuk;
          border-radius: $input-right-rounded-border;

          @media (max-width: $max-tablet-size) {
            border-radius: $input-radius-wuk;
          }
        }
      }

      &:first-of-type {
        input[type='radio'] + label {
          border-radius: $input-left-rounded-border;

          @media (max-width: $max-tablet-size) {
            border-radius: $input-radius-wuk;
          }
        }
      }

      input[type='radio'] {
        @include toggle-item-tick-box-wuk {
          content: none;
        }

        @include toggle-structure-root-wuk {
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          text-align: center;

          @media (max-width: $max-tablet-size) {
            text-align: left;
            justify-content: flex-start;
          }
        }

        &:disabled {
          @include toggle-structure-root-wuk {
            border-color: $input-disabled-border-color-wuk;
            background-color: $input-disabled-background-color-wuk;
            color: $input-disabled-text-color-wuk;
          }
        }
      }

      &:not(.minimal) {
        label {
          border: 1px solid transparent !important;
        }
      }
    }
  }
}
</style>
